<script lang="ts" setup>
import type { UseDownloadParams } from '~/composables/useDownload'

export interface DocumentsTableProps {
    data: DocumentGroup[]
}

const props = defineProps<DocumentsTableProps>()
const { $t } = useNuxtApp()

const tableScopePrefix = useId()
const tableColHeaderIds = {
    filename: `${tableScopePrefix}-col-filename`,
    published_from: `${tableScopePrefix}-col-published-from`,
    published_to: `${tableScopePrefix}-col-published-to`,
    action: `${tableScopePrefix}-col-action`,
} as const

const currentSortOrder = ref<'asc' | 'desc' | 'default'>('default')
const currentSortKey = ref<DocumentKeys>('filename')
const sortAnnouncement = ref<string>('')

const sortedData = computed<Document[]>(() =>
    props.data
        .map((item) => item.documents)
        .reduce((acc, curr) => [...acc, ...curr], [])
        .sort((a: Document, b: Document) => {
            let aField = a[currentSortKey.value] as string | number
            let bField = b[currentSortKey.value] as string | number
            if (currentSortKey.value === 'published_to') {
                aField = new Date(a.published_to as Date).getTime()
                bField = new Date(b.published_to as Date).getTime()

                if (currentSortOrder.value === 'default') return 0
                if (currentSortOrder.value === 'asc') return aField - bField
                if (currentSortOrder.value === 'desc') return bField - aField
            }

            if (currentSortOrder.value === 'default') return 0

            if (currentSortOrder.value === 'asc') {
                return aField < bField ? -1 : 1
            }

            return aField < bField ? 1 : -1
        })
)

const pdfSource = ref<string | undefined>(undefined)

const getColumnLabel = (key: DocumentKeys): string => {
    const labels: Partial<Record<DocumentKeys, string>> = {
        filename: $t('Názov dokumentu'),
        published_from: $t('Platnosť od'),
        published_to: $t('Platnosť do'),
    }
    return labels[key] || key
}

const getDocumentLabel = (document: Document): string => {
    return [
        document.filename,
        document.document.extension,
        document.document.size_human,
    ].filter(Boolean).join(', ')
}

const sort = ({ key }: { key: DocumentKeys }): void => {
    const newOrder = currentSortOrder.value === 'asc' ? 'desc' : 'asc'
    currentSortOrder.value = newOrder
    currentSortKey.value = key

    const orderLabel = newOrder === 'asc' ? $t('vzostupne') : $t('zostupne')
    sortAnnouncement.value = `${$t('Zoradené podľa')} ${getColumnLabel(key)}, ${orderLabel}`
}

const getAriaSortValue = (key: DocumentKeys): 'ascending' | 'descending' | 'none' => {
    if (currentSortKey.value !== key) return 'none'
    return currentSortOrder.value === 'asc' ? 'ascending' : 'descending'
}

const rowHeaderId = (rowIndex: number): string =>
    `${tableScopePrefix}-row-${rowIndex}`

const dataCellHeaders = (rowIndex: number, columnHeaderId: string): string =>
    `${rowHeaderId(rowIndex)} ${columnHeaderId}`

const handleClick = async ({
    url,
    filename,
}: UseDownloadParams): Promise<void> => {
    try {
        await useDownload({ url, filename })
    } catch (error: any) {
        if (error instanceof Error) {
            showError({ statusCode: 404, message: $t('Súbor neexistuje.') })
            useScrollIntoView(document.body)
        }
    }
}

const handleClickOnDocumentTitle = (e: MouseEvent, document: Document): void => {
    if(!document.show_pdf_as_booklet || window.matchMedia('(max-width: 425px)').matches) return
    e.preventDefault()
    pdfSource.value = document.document.path
}
</script>
<template>
  <div class="overflow-auto position-relative">
    <div
      aria-live="polite"
      aria-atomic="true"
      class="visually-hidden"
    >
      {{ sortAnnouncement }}
    </div>
    <Typo v-if="props.data.length === 0 || sortedData.length === 0">
      <p class="px-4 mt-4 mb-4">
        {{ $t('Nie sú dostupné žiadne dokumenty.') }}
      </p>
    </Typo>
    <table
      v-else
      class="documents-table w-100 bg-white"
    >
      <caption class="visually-hidden">
        {{ $t('Zoznam dokumentov - tabuľka') }}
      </caption>
      <thead>
        <tr class="d-none d-md-table-row">
          <th
            :id="tableColHeaderIds.filename"
            class="w-50"
            scope="col"
            :data-sort="
              currentSortKey === 'filename' ? currentSortOrder : ''
            "
            :aria-sort="getAriaSortValue('filename')"
          >
            <div class="documents-table-th-inner">
              {{ $t('Názov dokumentu') }}
              <button
                type="button"
                class="documents-table-sort-btn stretched-link"
                :aria-label="$t('Zoradiť podľa názvu dokumentu')"
                @click="() => sort({ key: 'filename' })"
              >
                <span
                  class="documents-table-sort"
                  aria-hidden="true"
                />
              </button>
            </div>
          </th>
          <th
            :id="tableColHeaderIds.published_from"
            class="text-nowrap"
            scope="col"
            :data-sort="
              currentSortKey === 'published_from'
                ? currentSortOrder
                : ''
            "
            :aria-sort="getAriaSortValue('published_from')"
          >
            <div class="documents-table-th-inner">
              {{ $t('Platnosť od') }}
              <button
                type="button"
                class="documents-table-sort-btn stretched-link"
                :aria-label="$t('Zoradiť podľa platnosti od')"
                @click="() => sort({ key: 'published_from' })"
              >
                <span
                  class="documents-table-sort"
                  aria-hidden="true"
                />
              </button>
            </div>
          </th>
          <th
            :id="tableColHeaderIds.published_to"
            class="text-nowrap"
            scope="col"
            :data-sort="
              currentSortKey === 'published_to'
                ? currentSortOrder
                : ''
            "
            :aria-sort="getAriaSortValue('published_to')"
          >
            <div class="documents-table-th-inner">
              {{ $t('Platnosť do') }}
              <button
                type="button"
                class="documents-table-sort-btn stretched-link"
                :aria-label="$t('Zoradiť podľa platnosti do')"
                @click="() => sort({ key: 'published_to' })"
              >
                <span
                  class="documents-table-sort"
                  aria-hidden="true"
                />
              </button>
            </div>
          </th>
          <th
            :id="tableColHeaderIds.action"
            class="text-end"
            scope="col"
          >
            <span class="visually-hidden">{{ $t('Akcia') }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(document, documentIndex) in sortedData"
          :key="`documents-table-item-${documentIndex}`"
        >
          <th
            :id="rowHeaderId(documentIndex)"
            scope="row"
            :headers="tableColHeaderIds.filename"
          >
            <div class="d-flex">
              <svg
                class="me-2 text-secondary flex-shrink-0"
                width="12"
                height="16"
                aria-hidden="true"
              >
                <use xlink:href="#file" />
              </svg>
              <div class="group">
                <Tracker :data="{ title: document.filename }">
                  <VNuxtLink
                    class="text-primary text-decoration-underline"
                    :to="document.document.path"
                    target="_blank"
                    type="application/pdf"
                    data-preview-disabled="true"
                    :aria-label="`${$t('Otvoriť dokument')} ${getDocumentLabel(document)}`"
                    :label="document.document.path"
                    @click="(e: MouseEvent) => handleClickOnDocumentTitle(e, document)"
                  >
                    {{ getDocumentLabel(document) }}
                  </VNuxtLink>
                </Tracker>
                <div class="fs-xsmall mt-2 d-md-none">
                  {{ $t('Platnosť od') }}
                  {{ $date(document.published_from?.toString()) }}
                  <template v-if="document.published_to">
                    do
                    {{
                      $date(document.published_to?.toString())
                    }}
                  </template>
                </div>
              </div>
            </div>
          </th>
          <td
            class="d-none d-md-table-cell"
            :headers="dataCellHeaders(documentIndex, tableColHeaderIds.published_from)"
          >
            <span class="visually-hidden"> 
              {{ `${$t('Dokument je v platnosti od ')} ${$date(document.published_from?.toString(), 'DD MMMM YYYY', true)}` }}
            </span>
            <span :aria-hidden="true">
              {{ $date(document.published_from?.toString()) }}
            </span>
          </td>
          <td
            class="d-none d-md-table-cell"
            :headers="dataCellHeaders(documentIndex, tableColHeaderIds.published_to)"
          >
            <span class="visually-hidden">
              {{ `${$t('Koniec platnosti dokumentu: ')} ${document.published_to ? $date(document.published_to?.toString(), 'DD MMMM YYYY', true) : $t('Nie je uvedený')}` }}
            </span>
            <span :aria-hidden="true">
              {{
                document.published_to
                  ? $date(document.published_to?.toString())
                  : '-'
              }}
            </span>
          </td>
          <td
            class="d-none d-md-table-cell text-secondary text-nowrap text-end"
            :headers="dataCellHeaders(documentIndex, tableColHeaderIds.action)"
          >
            <VNuxtLink
              :to="document.document.path"
              :label="`${$t('Stiahnuť')} ${document.filename}`"
              class="text-decoration-underline"
              role="button"
              :aria-label="`${$t('Stiahnuť')} ${getDocumentLabel(document)}`"
              data-preview-disabled="true"
              @click.prevent="
                handleClick({
                  url: document.document.path,
                  filename: `${document.filename}.pdf`,
                })
              "
            >
              <svg
                class="me-1"
                width="12"
                height="16"
                aria-hidden="true"
              >
                <use xlink:href="#download" />
              </svg>
              {{ $t('Stiahnuť') }}
            </VNuxtLink>
          </td>
        </tr>
      </tbody>
    </table>

    <IconsWrapper>
      <IconsSymbolDownload />
      <IconsSymbolFile />
    </IconsWrapper>

    <ClientOnly>
      <ModalPdfPreview
        v-if="pdfSource"
        :source="pdfSource"
        @close="pdfSource = undefined"
      />
    </ClientOnly>
  </div>
</template>
<style lang="scss" scoped>
@import './DocumentsTable.scss';
</style>
